/**
 * ATL-1477 — read EVERY row of a backend list, and prove it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS
 *
 * The backend's list endpoints page with `limit` (default 50) and `offset`, newest first. The
 * Supabase branches of the app's `*OnDateRange` readers sent one request, so a busy day came back as
 * its last 50 sales — and `generateProductReportForDate` / `validateDailyStats` WROTE figures computed
 * from that back to Firestore.
 *
 * Paging with `offset` does not fix it. The list changes while it is read: a void of a row already
 * read shifts the rest up and a row is skipped; a row whose sort key changes (a sale's date edited,
 * a client's `last_purchase_date` moving when they buy) jumps from the unread tail into the part
 * already read and is never seen. A `(date, id)` cursor removes the first and not the second, and a
 * cursor on the immutable `id` alone is unusably slow with a date filter (2.8 s per 200-row page of
 * a month of sales, measured on prod).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * HOW
 *
 * Each list has a `GET /<list>/window?idFrom&idTo` route (backend `id-window.util.ts`). It returns
 * the rows whose primary key is in `[idFrom, idTo)` — at most `WINDOW_MAX_ROWS` — and `total`, the
 * exact number of rows that window holds, counted by Postgres IN THE SAME STATEMENT. So:
 *
 * - `rows.length === total`: the response is the whole window at one instant. Proven.
 * - `total > rows.length`: the window holds more than one response carries; it is split into
 *   narrower windows and each is read the same way.
 *
 * An id never changes, so no insert, void, edit or date change can move a row from one window to
 * another. Every row that matches the filters for the whole duration of the read lies in exactly
 * one window and is returned when that window is read. Nothing is restarted, nothing is skipped,
 * and a row inserted or removed mid-read is simply seen or not — the same as a read made a moment
 * earlier or later.
 *
 * Every response is checked: the echoed bounds are the ones asked for, every row's id lies inside
 * them, the row count matches `total`, and no id appears twice. Any failure throws. There is no
 * path that returns a list it has not proven complete.
 *
 * Windows are ranges of the first 48 bits of the UUID (plain Numbers — no BigInt dependency on the
 * device runtime). Postgres orders uuids by their bytes, which is the order of their lowercase hex,
 * so a 48-bit prefix range is exactly an id range.
 */
import type { IdWindowPage, IdWindowParams } from '@atlas/api-client'

/** Rows a window route returns at most; must equal the backend's `ID_WINDOW_MAX_ROWS`. */
export const WINDOW_MAX_ROWS = 200

/** Windows read at once. */
export const WINDOW_CONCURRENCY = 4

/** Past this many windows the read is not converging; it throws rather than continue. */
export const MAX_WINDOWS = 20000

/** Size of the window key space: the first 48 bits of a UUID. */
export const KEY_SPACE = 2 ** 48

const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/** A window over the 48-bit id prefix: `lo <= prefix < hi`. */
interface KeyWindow {
  lo: number
  hi: number
}

export type WindowFetcher<T> = (window: IdWindowParams) => Promise<IdWindowPage<T>>

export interface ReadWholeListOptions<T> {
  /** Names the list in errors, e.g. `'sales'`. */
  label: string
  /** Order of the returned list (windows arrive in id order, not in the list's order). */
  order: (a: T, b: T) => number
}

const prefixOf = (id: string): number => parseInt(id.slice(0, 8) + id.slice(9, 13), 16)

const boundUuid = (key: number): string => {
  const hex = key.toString(16).padStart(12, '0')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-0000-0000-000000000000`
}

/** The query parameters for a window; an unbounded side is omitted. */
export const windowParams = ({ lo, hi }: KeyWindow): IdWindowParams => ({
  ...(lo > 0 ? { idFrom: boundUuid(lo) } : {}),
  ...(hi < KEY_SPACE ? { idTo: boundUuid(hi) } : {}),
})

/** The row's Supabase UUID — the key every window is defined over. */
export const rowUuid = (row: unknown, label: string): string => {
  const id = row && typeof row === 'object' ? (row as { supabaseId?: unknown }).supabaseId : undefined
  if (typeof id !== 'string' || !CANONICAL_UUID.test(id)) {
    throw new Error(`readWholeList(${label}): a row has no lowercase supabaseId UUID, so its window cannot be checked`)
  }
  return id
}

/** Split a window that holds `total` rows into parts expected to hold about half a response each. */
const split = (window: KeyWindow, total: number, label: string): KeyWindow[] => {
  const width = window.hi - window.lo
  if (width < 2) {
    throw new Error(
      `readWholeList(${label}): ${total} rows share one 48-bit id prefix; the window cannot be split further`,
    )
  }
  const parts = Math.min(width, Math.max(2, Math.min(64, Math.ceil(total / (WINDOW_MAX_ROWS / 2)))))
  const step = Math.floor(width / parts)
  const out: KeyWindow[] = []
  for (let i = 0; i < parts; i += 1) {
    out.push({ lo: window.lo + i * step, hi: i === parts - 1 ? window.hi : window.lo + (i + 1) * step })
  }
  return out
}

/**
 * Read every row of a list through its window route, or throw. `fetchWindow` must pass the window
 * bounds, together with the list's filters, to the list's `.../window` route.
 */
export async function readWholeList<T>(
  fetchWindow: WindowFetcher<T>,
  { label, order }: ReadWholeListOptions<T>,
): Promise<T[]> {
  const pending: KeyWindow[] = [{ lo: 0, hi: KEY_SPACE }]
  const rows: T[] = []
  const seen = new Set<string>()
  let windowsRead = 0

  const readOne = async (window: KeyWindow): Promise<void> => {
    windowsRead += 1
    if (windowsRead > MAX_WINDOWS) {
      throw new Error(`readWholeList(${label}): more than ${MAX_WINDOWS} windows; refusing to continue`)
    }
    const asked = windowParams(window)
    const page = await fetchWindow(asked)

    if (!page || !Array.isArray(page.rows) || !Number.isInteger(page.total) || page.total < 0) {
      throw new Error(`readWholeList(${label}): the window response has no rows/total`)
    }
    if ((page.idFrom ?? undefined) !== asked.idFrom || (page.idTo ?? undefined) !== asked.idTo) {
      throw new Error(`readWholeList(${label}): the backend answered a different window than the one asked for`)
    }
    for (const row of page.rows) {
      const key = prefixOf(rowUuid(row, label))
      if (key < window.lo || key >= window.hi) {
        throw new Error(`readWholeList(${label}): the backend returned a row outside the window it was asked for`)
      }
    }
    if (page.rows.length !== Math.min(page.total, WINDOW_MAX_ROWS)) {
      throw new Error(
        `readWholeList(${label}): a window holding ${page.total} rows returned ${page.rows.length}; it cannot be proven complete`,
      )
    }

    if (page.total > page.rows.length) {
      pending.push(...split(window, page.total, label))
      return
    }
    for (const row of page.rows) {
      const id = rowUuid(row, label)
      if (seen.has(id)) {
        throw new Error(`readWholeList(${label}): row ${id} arrived in two disjoint windows`)
      }
      seen.add(id)
      rows.push(row)
    }
  }

  // Up to WINDOW_CONCURRENCY windows in flight; a split pushes its parts back onto `pending`.
  //
  // A task never rejects: it records its failure in `failure`, and the loop checks `failure` after
  // EVERY settle. Racing rejecting tasks instead would lose an error whenever another window
  // happened to settle first — the failed window's rows would simply be missing from a list that
  // then returned normally. (`then`, not `finally`: only the core Promise surface is assumed on
  // device — ATL-980.)
  const inFlight = new Set<Promise<void>>()
  const outcome: { failed: boolean; error: unknown } = { failed: false, error: undefined }
  while (!outcome.failed && (pending.length > 0 || inFlight.size > 0)) {
    let window = inFlight.size < WINDOW_CONCURRENCY ? pending.pop() : undefined
    while (window !== undefined) {
      const task: Promise<void> = readOne(window).then(
        () => {
          inFlight.delete(task)
        },
        (error: unknown) => {
          inFlight.delete(task)
          if (!outcome.failed) {
            outcome.failed = true
            outcome.error = error
          }
        },
      )
      inFlight.add(task)
      window = inFlight.size < WINDOW_CONCURRENCY ? pending.pop() : undefined
    }
    await Promise.race(inFlight)
  }
  if (outcome.failed) {
    // Nothing partial is ever returned. Windows still in flight are abandoned; their tasks cannot
    // reject, so none surfaces as an unhandled rejection.
    throw outcome.error
  }

  return rows.sort(order)
}

// ── Ordering ─────────────────────────────────────────────────────────────────────────────────────

/** Milliseconds of a date as the API delivers it: a Date, a TimestampLike, `{seconds}`, or ISO text. */
export const toMillis = (value: unknown): number | null => {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'object') {
    const v = value as { toDate?: () => Date; seconds?: number; _seconds?: number; nanoseconds?: number }
    if (typeof v.toDate === 'function') return v.toDate().getTime()
    const seconds = v.seconds ?? v._seconds
    if (typeof seconds === 'number') return seconds * 1000 + Math.floor((v.nanoseconds ?? 0) / 1e6)
    return null
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value)
    return Number.isNaN(ms) ? null : ms
  }
  if (typeof value === 'number') return value
  return null
}

/**
 * The backend lists' own order: `field` descending, then id descending; rows without a date last.
 */
export const newestFirst = <T>(field: string) => (a: T, b: T): number => {
  const ta = toMillis((a as Record<string, unknown>)[field])
  const tb = toMillis((b as Record<string, unknown>)[field])
  if (ta !== tb) {
    if (ta === null) return 1
    if (tb === null) return -1
    return tb - ta
  }
  const ia = String((a as Record<string, unknown>).supabaseId ?? '')
  const ib = String((b as Record<string, unknown>).supabaseId ?? '')
  return ia < ib ? 1 : ia > ib ? -1 : 0
}
